import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Button, Grid, Card, CardContent, Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { getLocalBusinessSchema } from '../components/StructuredData';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (videoElement) {
      const handleCanPlay = () => {
        setVideoLoaded(true);
      };
      
      const handleVideoEnded = () => {
      };
      
      videoElement.addEventListener('canplay', handleCanPlay);
      videoElement.addEventListener('ended', handleVideoEnded);
      
      if (videoElement.readyState >= 3) {
        setVideoLoaded(true);
      }
      
      return () => {
        videoElement.removeEventListener('canplay', handleCanPlay);
        videoElement.removeEventListener('ended', handleVideoEnded);
      };
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        setShowFloatingBtn(heroRef.current.getBoundingClientRect().bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      key: 'quality',
      title: t('home.features.quality.title'),
      description: t('home.features.quality.description')
    },
    {
      key: 'experience',
      title: t('home.features.experience.title'),
      description: t('home.features.experience.description')
    },
    {
      key: 'comfort',
      title: t('home.features.comfort.title'),
      description: t('home.features.comfort.description')
    }
  ];

  return (
    <Box sx={{ pb: { xs: 12, sm: 6 } }}>
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        canonicalPath="/"
        jsonLd={[getLocalBusinessSchema()]}
      />

      {/* Hero Section with Background Video */}
      <Box
        ref={heroRef}
        sx={{
          height: { xs: '60vh', sm: '50vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/main-vlc-image.webp"
          alt="Valencia City"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'scale-down',
            zIndex: 0,
            opacity: videoLoaded ? 0 : 1,
            transition: 'opacity 0.5s ease-in-out',
          }}
        />
      
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
            overflow: 'hidden',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          >
            <source src="/videos/home-video.webm" type="video/webm" />
            <source src="/videos/home-video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1,
          }}
        />

        <Container>
          <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Typography variant="h1" sx={{ fontSize: 0, lineHeight: 0, m: 0, p: 0, visibility: 'hidden', position: 'absolute' }}>
              XperienceTattoo - Premium Tattoo Experiences in Valencia
            </Typography>
            <Box
              component="img"
              src="/images/xperience-tattoo-logo.png"
              alt="Xperience Tattoo"
              sx={{
                maxWidth: { xs: '90%', sm: '80%' },
                height: 'auto',
                marginBottom: { xs: 2, sm: 4 }
              }}
            />
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/experiences')}
              sx={{
                mt: { xs: 2, sm: 4 },
                color: 'white',
                borderColor: 'white',
                borderRadius: '50px',
                padding: { xs: '8px 20px', sm: '10px 30px' },
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                backgroundColor: 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                  transform: 'scale(1.05)',
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              {t('home.cta')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box sx={{ minHeight: { xs: 'auto', sm: '100vh' }, display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: { xs: 2, sm: 4 },
            paddingBottom: { xs: 1, sm: 2 },
            backgroundColor: '#f8f8f8'
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: { xs: 2, sm: 4 }, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {t('home.about.title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, maxWidth: '900px', mx: 'auto' }}>
              {t('home.about.paragraph1')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, maxWidth: '900px', mx: 'auto' }}>
              {t('home.about.paragraph2')}
            </Typography>
          </Container>
        </Box>
        
        <Box
          sx={{
            width: '100%',
            height: '40vh',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            component="img"
            src="/images/main-vlc-image.webp"
            alt={t('home.about.imageAlt')}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'scale-down',
            }}
          />
        </Box>
      </Box>

      <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ pt: 4, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        {t('home.features.title')}
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 10, sm: 8 }, px: { xs: 2, sm: 0 } }}>
        {features.map((feature) => (
          <Grid item key={feature.key} xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h3" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {feature.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Fade in={showFloatingBtn}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 120, sm: 80 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/experiences')}
            sx={{
              minWidth: { xs: '230px', sm: '250px' },
              borderRadius: '50px',
              padding: { xs: '12px 28px', sm: '14px 36px' },
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '1rem' },
              fontWeight: 600,
              color: 'white',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
              border: '2px solid rgba(255,255,255,0.8)',
              '&:hover': {
                background: 'linear-gradient(135deg, #333 0%, #555 100%)',
                border: '2px solid white',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            {t('home.cta')}
          </Button>
        </Box>
      </Fade>
    </Box>
  );
};

export default Home;
